import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CopyableContainer from "./CopyableContainer";
import PendingDataNotice from "./PendingDataNotice";
import LeagueTabHeading from "./LeagueTabHeading";
import LeagueTabSkeleton from "./LeagueTabSkeleton";
import SimulationPlayoffSummary, { getSimulationRowClassName } from "./SimulationPlayoffSummary";
import PlayoffTableImageCopyButton from "./PlayoffTableImageCopyButton";
import { seedingOutcomesImageOptions } from "../utils/playoffTableImage";
import { probabilityHeatStyle } from "../utils/simulationHeatmap";
import './styles/tableStyles.css';

const SeedingOutcomesTable = ({ data, playoffTeams, selectedWeek, pendingData }) => {
    const [sortConfig, setSortConfig] = useState(null);
    const sortedRows = useMemo(() => {
        if (!Array.isArray(data)) return [];

        const rows = data.map((team, originalIndex) => ({ team, originalIndex }));
        if (!sortConfig) return rows;

        const direction = sortConfig.direction === "ascending" ? 1 : -1;
        return rows.sort((left, right) => {
            const leftValue = Number(left.team[sortConfig.key]);
            const rightValue = Number(right.team[sortConfig.key]);
            const numericDifference =
                (Number.isFinite(leftValue) ? leftValue : 0) -
                (Number.isFinite(rightValue) ? rightValue : 0);

            return numericDifference === 0
                ? left.originalIndex - right.originalIndex
                : numericDifference * direction;
        });
    }, [data, sortConfig]);

    if (data === null || playoffTeams === null) {
        return <LeagueTabSkeleton variant="seeding-outcomes" label="Calculating seeding outcomes" />;
    }

    function formatPercent(val) {
        const percentage = val * 100;
        return `${percentage.toFixed(val < 0.1 ? 1 : 0)}%`;
    }

    const handleSort = (key) => {
        setSortConfig((current) => ({
            key,
            direction:
                current?.key === key && current.direction === "descending"
                    ? "ascending"
                    : "descending",
        }));
    };

    const sortableHeader = (label, key, content) => {
        const isActive = sortConfig?.key === key;
        const direction = isActive ? sortConfig.direction : null;
        const nextDirection = direction === "descending" ? "ascending" : "descending";

        return (
            <th aria-sort={direction || undefined}>
                <button
                    type="button"
                    className={`simulation-sort-button ${isActive ? "simulation-sort-button--active" : ""}`}
                    aria-label={`Sort by ${label} ${nextDirection}`}
                    onClick={() => handleSort(key)}
                >
                    <span>{content}</span>
                    <span className="simulation-sort-indicator" aria-hidden="true">
                        {direction === "ascending" ? "\u2191" : direction === "descending" ? "\u2193" : "\u2195"}
                    </span>
                </button>
            </th>
        );
    };

    return (
        <CopyableContainer
            title={
                <div className="rank-distribution-heading">
                    <LeagueTabHeading title="Seeding Outcomes" />
                    <PlayoffTableImageCopyButton
                        imageOptions={seedingOutcomesImageOptions({
                            data,
                            playoffTeams,
                            selectedWeek,
                        })}
                        fileName={`seeding-outcomes-week-${selectedWeek}`}
                        disabled={!Array.isArray(data)}
                    />
                </div>
            }
            fileName="seeding-outcomes"
            className="simulation-copyable-shell playoff-table-image-copyable seeding-outcomes-copyable"
        >
            <PendingDataNotice
                dataType="seeding outcomes"
                isPending={pendingData}
            />
            <div className="table-rounded-shell">
            <table className="table-with-bottom-caption">
                <thead>
                    <tr>
                        <th className="team-name-column">Team</th>
                        <th className="team-name-column">Owner</th>
                        {sortableHeader("First in League", "first_in_league", <>First<br />in League</>)}
                        {sortableHeader("First in Division", "first_in_division", <>First<br />in Division</>)}
                        {sortableHeader("Make Playoffs", "make_playoffs", "Make Playoffs")}
                        {sortableHeader("Last in Division", "last_in_division", <>Last<br />in Division</>)}
                        {sortableHeader("Last in League", "last_in_league", <>Last<br />in League</>)}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map(({ team, originalIndex }, displayIndex) => {
                        const outcomeClassName = getSimulationRowClassName(
                            originalIndex,
                            playoffTeams,
                            false
                        )
                            .split(" ")
                            .filter((className) => className.startsWith("simulation-row-"))
                            .join(" ");

                        return (
                        <tr
                            key={originalIndex}
                            className={`${displayIndex % 2 === 0 ? "even-row" : "odd-row"}${outcomeClassName ? ` ${outcomeClassName}` : ""}`}
                        >
                            <td className="team-name-column">
                                {team.team}
                            </td>
                            <td className="team-name-column">
                                {team.owner}
                            </td>
                            {[
                                team.first_in_league,
                                team.first_in_division,
                                team.make_playoffs,
                                team.last_in_division,
                                team.last_in_league,
                            ].map((value, outcomeIndex) => (
                                <td key={outcomeIndex} className="simulation-heat-cell">
                                    <div className="simulation-heat" style={probabilityHeatStyle(value)}>
                                        {formatPercent(value)}
                                    </div>
                                </td>
                            ))}
                        </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
            <SimulationPlayoffSummary playoffTeams={playoffTeams} showByeChip={false} />
            <p className="simulation-table-disclaimer">
                    All percentages are based on simulation results alone.
                    Values of 0% or 100% do not necessarily mean that a team has
                    been mathematically eliminated or clinched a playoff spot.
            </p>
        </CopyableContainer>
    );
};

SeedingOutcomesTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            team: PropTypes.string.isRequired,
            owner: PropTypes.string.isRequired,
            first_in_league: PropTypes.string.isRequired,
            first_in_division: PropTypes.string.isRequired,
            make_playoffs: PropTypes.string.isRequired,
            last_in_division: PropTypes.string.isRequired,
            last_in_league: PropTypes.string.isRequired,
        })
    ),
    playoffTeams: PropTypes.number.isRequired, // Add playoffTeams as a required prop
    selectedWeek: PropTypes.number,
};

export default SeedingOutcomesTable;
